// AdminDashboard.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import Layout from "../../components/Layout/Layout";
import {
  FiUsers,
  FiPackage,
  FiDollarSign,
  FiAlertTriangle,
} from "react-icons/fi";

// Import components
import StatCard from "../../components/Dashboard/StatsCard";
import Sidebar from "../../components/Dashboard/Sidebar";
import LowStockAlert from "../../components/Dashboard/LowStockAlert";
import LowStockTable from "../../components/Dashboard/LowStockTable";
import SalesChart from "../../components/Dashboard/SalesChart";
import TopProductsPieChart from "../../components/Dashboard/TopProductsPieChart";
import BestSellingProducts from "../../components/Dashboard/BestSellingProducts";
import SalesBreakdown from "../../components/Dashboard/SalesBreakdown";

// Import custom hooks
import { useDashboardStats } from "../../hooks/useDashboardStats";
import { useLowStockProducts } from "../../hooks/useLowStockProducts";

const AdminDashboard = () => {
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  // Use custom hooks
  const { stats, loading } = useDashboardStats(auth?.token);
  const { lowStockProducts } = useLowStockProducts(auth?.token);

  // Prepare data for charts
  const salesData = [
    { period: "Daily", sales: stats.dailySales },
    { period: "Weekly", sales: stats.weeklySales },
    { period: "Monthly", sales: stats.monthlySales },
  ];

  const topProductsData = stats.topProducts.map((product) => ({
    name: product.name,
    value: product.salesCount,
  }));

  const formatCurrency = (amount) =>
    amount.toLocaleString("en-IN", { style: "currency", currency: "INR" });

  const handleLogout = () => {
    setAuth({ user: null, token: "" });
    localStorage.removeItem("auth");
    navigate("/login");
    setTimeout(() => window.location.reload(), 100);
  };

  const scrollToLowStock = () => {
    const element = document.getElementById("low-stock-section");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <Layout title="Admin Dashboard">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Admin Dashboard">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Welcome back, {auth?.user?.name}!
              </p>
            </div>
            {/* Menu toggle button removed here */}
          </div>

          {/* Low Stock Alert */}
          <LowStockAlert
            count={lowStockProducts.length}
            onViewDetails={scrollToLowStock}
          />

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onLogout={handleLogout}
              />
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {activeTab === "overview" && (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                      icon={FiUsers}
                      title="Total Users"
                      value={stats.totalUsers}
                      subtitle="Registered customers"
                      color="#1a1a1a"
                    />
                    <StatCard
                      icon={FiPackage}
                      title="Total Orders"
                      value={stats.totalOrders}
                      subtitle="All time orders"
                      color="#404040"
                    />
                    <StatCard
                      icon={FiDollarSign}
                      title="Weekly Sales"
                      value={formatCurrency(stats.weeklySales)}
                      subtitle="Last 7 days"
                      color="#737373"
                    />
                    <StatCard
                      icon={FiAlertTriangle}
                      title="Low Stock Alert"
                      value={lowStockProducts.length}
                      subtitle="Products need attention"
                      color="#dc2626"
                      alert={lowStockProducts.length > 0}
                    />
                  </div>

                  {/* Charts Section */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <SalesChart
                      data={salesData}
                      formatCurrency={formatCurrency}
                    />
                    <TopProductsPieChart data={topProductsData} />
                  </div>

                  {/* Low Stock Products Table */}
                  <LowStockTable products={lowStockProducts} />

                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <BestSellingProducts products={stats.topProducts} />
                    <SalesBreakdown
                      stats={stats}
                      formatCurrency={formatCurrency}
                    />
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
